import React, { useEffect, useState } from "react";
import { api, resolveImageUrl } from "@/lib/api";
import PartnerDetailDialog from "@/components/PartnerDetailDialog";
import { Sparkles } from "lucide-react";

/**
 * Continuous horizontal marquee of partners. Placed at the very top of the
 * B2C homepage so members see live partner offers on page load.
 * - Pure CSS animation, GPU-accelerated (translateX only).
 * - Content is duplicated for seamless infinite scroll.
 * - Hover pauses the strip; click opens the partner detail dialog.
 */
export default function PartnersMarquee({ speed = 60 }) {
  const [partners, setPartners] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/partners");
        setPartners((data || []).slice(0, 20));
      } catch (_) { /* silent */ }
    })();
  }, []);

  if (!partners.length) return null;

  // Duplicate list to allow seamless loop
  const loop = [...partners, ...partners];
  // Total scroll duration = list length * speed
  const dur = partners.length * (speed / 10); // e.g. 8 partners * 6 = 48s

  return (
    <>
      <section
        data-testid="partners-marquee"
        className="relative overflow-hidden pt-[76px] md:pt-[80px] pb-3 md:pb-4"
        style={{
          background: "var(--brand-primary-dark, #060a12)",
          borderBottom: "2px solid var(--brand-accent, #BFFF00)",
        }}
        aria-label="Partenaires locaux"
      >
        {/* Left/right edge fades */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-16 z-10"
          style={{ background: "linear-gradient(to right, var(--brand-primary-dark, #060a12), transparent)" }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-16 z-10"
          style={{ background: "linear-gradient(to left, var(--brand-primary-dark, #060a12), transparent)" }}
        />

        <div className="flex items-center gap-3">
          {/* Fixed kicker on the left */}
          <div className="shrink-0 pl-4 md:pl-6 pr-4 flex items-center gap-2 text-[10px] md:text-[11px] uppercase tracking-widest font-bold whitespace-nowrap"
               style={{ color: "var(--brand-accent, #BFFF00)" }}>
            <Sparkles className="w-3 h-3 md:w-3.5 md:h-3.5 animate-pulse" />
            <span className="hidden sm:inline">Le cercle · Live</span>
            <span className="sm:hidden">Live</span>
          </div>

          {/* Scrolling track */}
          <div className="relative flex-1 overflow-hidden">
            <div
              className="marquee-track flex gap-3 md:gap-4 items-stretch"
              style={{ animationDuration: `${dur}s` }}
            >
              {loop.map((p, i) => (
                <MarqueeChip
                  key={`${p.id}-${i}`}
                  partner={p}
                  onClick={() => setActive(p)}
                  testId={`marquee-chip-${i}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <PartnerDetailDialog partner={active} open={!!active} onOpenChange={(v) => !v && setActive(null)} />
    </>
  );
}

function MarqueeChip({ partner, onClick, testId }) {
  const val = partner.offer_value || 0;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group shrink-0 flex items-center gap-2.5 pl-2 pr-3.5 py-1.5 rounded-full transition-all hover:scale-[1.03] hover:shadow-lg text-left"
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <span
        className="w-8 h-8 md:w-9 md:h-9 rounded-full overflow-hidden shrink-0 bg-white/10 flex items-center justify-center"
      >
        {partner.image_url ? (
          <img
            src={resolveImageUrl(partner.image_url)}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-white/50 text-xs font-bold">{(partner.name || "?").slice(0, 1)}</span>
        )}
      </span>
      <span className="flex flex-col leading-tight min-w-0">
        <span className="text-white text-xs md:text-sm font-bold whitespace-nowrap group-hover:text-white">
          {partner.name}
        </span>
        <span className="text-[10px] md:text-[11px] text-white/50 uppercase tracking-wider whitespace-nowrap">
          {partner.category}
        </span>
      </span>
      {val > 0 && (
        <span
          className="ml-1 px-2 py-0.5 rounded-full text-[10px] md:text-[11px] font-black whitespace-nowrap"
          style={{ background: "var(--brand-accent, #BFFF00)", color: "var(--brand-primary, #0b1220)" }}
        >
          −{val}€
        </span>
      )}
    </button>
  );
}
